import { FolderCreationModel, createFolder } from "client/Api/Documents/SharePoint/sharePointAction";
import { HttpRequestError } from "client/Api/httpClient";
import {
	Dialog,
	Form,
	FormItem,
	TextInput,
	Toolbar,
	ValidationError,
	renderReactDialog
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { useUnmountedCancellation } from "client/Hooks";
import { isNil } from "client/Misc/utils";
import {
	IRootValidation,
	InputValidationResult,
	InputValidationResultKind,
	convertServerErrors,
	createValidationConfiguration
} from "client/Validation";
import { inline } from "client/Validation/rules";
import { SharePointEvent, publish } from "client_legacy/Documents/SharePoint/documentEvents";
import * as namePolicy from "client_legacy/Documents/SharePoint/documentNamePolicy";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import {
	DocumentsErrorModel,
	FolderViewModel
} from "client/Api/Documents/SharePoint/common";
import { Folderish } from "client_legacy/Documents/SharePoint/models";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { ClientValidationErrorModel } from "client/Api/common";
import {
	ReactElement,
	useCallback,
	useState
} from "react";

export type NewFolderDialogProps = {
	parentFolder: Folderish;
	onClose: () => void;
};

export const NewFolderDialog = (props: NewFolderDialogProps): ReactElement => {
	const {
		parentFolder,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<FormData>({ name: "" });

	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);
			const validation: IRootValidation<FormData> = formHandler.validation;
			try {
				const validationResult: InputValidationResult = await validation.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				const model: FolderCreationModel = {
					name: formData.name
						.trimEnd(),
					location: convertToLocationModel(parentFolder.location),
					folderId: parentFolder.id
				};

				const createdFolder: FolderViewModel = await createFolder(model, globalCancellation);

				publish(
					SharePointEvent.AddedOrReplaced,
					createdFolder
				);

				onClose();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				let errorMessage: string | undefined;
				if (e instanceof HttpRequestError && e.statusCode === 400) {
					try {
						const errorModel: DocumentsErrorModel = e.readJson<DocumentsErrorModel>();
						if ("message" in errorModel) {
							errorMessage = errorModel.message;
						}
					} catch (exception) {
						console.error(exception);
					}
				}

				if (isNil(errorMessage)) {
					console.error(e);
					errorMessage = localize("_GeneralErrorMessage");
				}

				const errors: readonly ClientValidationErrorModel[] = [
					{
						propertyName: "name",
						errorMessage: errorMessage
					}
				];

				validation.setServerErrors(
					convertServerErrors(errors)
				);

				setIsBusy(false);
			}
		},
		[
			parentFolder.id,
			parentFolder.location,
			formData.name,
			formHandler.validation,
			globalCancellation,
			isBusy,
			onClose
		]
	);

	return (
		<Dialog
			trackingId="sp-new-folder-dialog"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={localize("_CreateANewFolder")}
				onClose={onClose}
			/>

			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem
						label={localize("_FolderName")}
						isRequired
					>
						<TextInput
							{...formHandler.for("name").attachToTextInput()}
							placeholder={localize("_EnterFolderName")}
							autoFocus="focus"
						/>

						<ValidationError {...formHandler.for("name").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						trackingId="done"
						text={localize("_Done")}
						onClick={submitHandler}
					/>

					<Toolbar.Button
						kind="secondary"
						trackingId="cancel"
						text={localize("_Cancel")}
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
NewFolderDialog.displayName = "NewFolderDialog";

type FormData = {
	name: string;
};

const validationConfiguration = createValidationConfiguration<FormData>();

validationConfiguration.forScalar("name")
	.add(
		inline(
			async (value): Promise<InputValidationResult> => {
				if (value.trim() === "") {
					return {
						kind: InputValidationResultKind.Invalid,
						errorMessage: localize("_FillInFolderName")
					};
				}

				const illegalCharacters: string[] = await namePolicy.getIllegalCharacters();
				if (!namePolicy.isValid(value, illegalCharacters)) {
					const illegalCharactersString: string = illegalCharacters.join(" ");

					return {
						kind: InputValidationResultKind.Invalid,
						errorMessage: localize("_ContainsIllegalCharacters")
							.replace("{0}", illegalCharactersString)
					};
				}

				return {
					kind: InputValidationResultKind.Valid
				};
			}
		)
	);

export function openNewFolderDialog(
	props: Omit<NewFolderDialogProps, "onClose">
): void {
	return renderReactDialog(
		(onClose): ReactElement => (
			<NewFolderDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}
